<script setup lang="ts">
import { Head } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import EstateObjectData = App.Data.EstateObject.EstateObjectData
import AttachmentData = App.Data.AttachmentData
import ValueDisplay from '@/components/ValueDisplay.vue'
import FormGroup from '@/components/FormGroup.vue'
import { computed } from 'vue'
import LastPage from '@/components/LastPage.vue'
import type { AmountUnit } from '@/utils/amount-unit'

const { t } = useI18n()

const props = defineProps<{
  estateObject: EstateObjectData
  documents: AttachmentData[]
  photos: AttachmentData[]
  owner: string | null
}>()

const ownerLabelKey = computed(() => {
  return props.estateObject.is_purchase ? 'form.owner.purchase.label' : 'form.owner.portfolio.label'
})

/** Grunderwerbsteuer, Maklerprovision and Notarkosten are stored either as a percentage or as a euro amount. */
const amountDisplay = (
  value: number | null | undefined,
  unit: AmountUnit | null | undefined,
): { value: number | null; format: 'currency' | 'percent' } => {
  if (value == null) {
    return { value: null, format: 'percent' }
  }

  return unit === 'euro' ? { value, format: 'currency' } : { value: value / 100, format: 'percent' }
}
</script>

<template>
  <div class="mx-auto w-full max-w-[210mm]">
    <Head :title="estateObject.title" />
    <div class="mb-8">
      <h1 class="text-2xl font-semibold">{{ estateObject.title }}</h1>
      <div>
        {{ estateObject.address }}
      </div>
    </div>

    <div v-if="photos.length" class="bg-surface-50 flex justify-between gap-4 p-4">
      <div v-for="photo in photos.slice(0, 5)" :key="photo.id" class="bg-surface-200 relative overflow-hidden rounded">
        <TwicImg :src="photo.path" class="size-[136px] object-cover" />
      </div>
    </div>

    <FormGroup :legend="t('form.general.label')">
      <ValueDisplay
        :label="t('form.estate_object_type.label')"
        :value="estateObject.estate_object_type"
        format="enum"
        enum-translation="estateObjectType" />
      <ValueDisplay :label="t(ownerLabelKey)" :value="owner" />
      <ValueDisplay :label="t('form.construction_year.label')" :value="estateObject.construction_year" />
      <ValueDisplay
        :label="t('form.property_size.label')"
        :value="estateObject.property_size"
        format="number"
        :suffix="` ${t('units.square_meter')}`" />
      <ValueDisplay
        :label="t('form.living_space_size.label')"
        :value="estateObject.living_space_size"
        format="number"
        :suffix="` ${t('units.square_meter')}`" />
      <ValueDisplay
        :label="t('form.business_space_size.label')"
        :value="estateObject.business_space_size"
        format="number"
        :suffix="` ${t('units.square_meter')}`" />
      <ValueDisplay
        :label="t('form.business_type.label')"
        :value="estateObject.business_type"
        format="enum"
        enum-translation="businessType" />
      <ValueDisplay :label="t('form.purchase_price.label')" :value="estateObject.purchase_price" format="currency" />
      <ValueDisplay
        :label="t('form.land_register_charge.label')"
        v-bind="amountDisplay(estateObject.land_register_charge, estateObject.land_register_charge_unit)" />
      <ValueDisplay
        :label="t('form.broker_commission.label')"
        v-bind="amountDisplay(estateObject.broker_commission, estateObject.broker_commission_unit)" />
      <ValueDisplay
        :label="t('form.notary_land_registry_fees.label')"
        v-bind="amountDisplay(estateObject.notary_land_registry_fees, estateObject.notary_land_registry_fees_unit)" />
      <ValueDisplay :label="t('form.equity.label')" :value="estateObject.equity" format="currency" />
    </FormGroup>

    <div class="pagebreak" />

    <!--    <LoanTable v-if="!form.is_purchase" v-model:form="form" />-->

    <FormGroup :legend="t('form.details.label')">
      <ValueDisplay :label="t('form.number_of_floors.label')" :value="estateObject.number_of_floors" />
      <ValueDisplay :label="t('form.number_of_parking_spaces.label')" :value="estateObject.number_of_parking_spaces" />
      <ValueDisplay :label="t('form.number_of_garages.label')" :value="estateObject.number_of_garages" />
      <ValueDisplay
        :label="t('form.heating_type.label')"
        :value="estateObject.heating_type"
        format="enum"
        enum-translation="heating_type" />
      <ValueDisplay
        :label="t('form.energy_source_type.label')"
        :value="estateObject.energy_source_type"
        format="enum"
        enum-translation="energy_source_type" />
      <ValueDisplay
        :label="t('form.roof_type.label')"
        :value="estateObject.roof_type"
        format="enum"
        enum-translation="roof_type" />
      <ValueDisplay
        :label="t('form.window_type.label')"
        :value="estateObject.window_type"
        format="enum"
        enum-translation="window_type" />
      <ValueDisplay
        :label="t('form.elevator_type.label')"
        :value="estateObject.elevator_type"
        format="enum"
        enum-translation="elevator_type" />
    </FormGroup>

    <FormGroup :legend="t('form.attributes.label')">
      <ValueDisplay
        :label="t('form.has_monument_protection.label')"
        :value="estateObject.has_monument_protection"
        format="boolean" />
      <ValueDisplay :label="t('form.has_gambling.label')" :value="estateObject.has_gambling" format="boolean" />
      <ValueDisplay :label="t('form.has_leasehold.label')" :value="estateObject.has_leasehold" format="boolean" />
      <ValueDisplay
        :label="t('form.has_mountain_damage_waiver.label')"
        :value="estateObject.has_mountain_damage_waiver"
        format="boolean" />
      <ValueDisplay
        :label="t('form.has_homeowners_association.label')"
        :value="estateObject.has_homeowners_association"
        format="boolean" />
      <ValueDisplay
        :label="t('form.has_energy_certificate.label')"
        :value="estateObject.energy_certificate_type"
        format="enum"
        enum-translation="energyCertificateType" />
    </FormGroup>

    <div class="pagebreak" />

    <FormGroup :legend="t('form.actual_rent_group.label')">
      <ValueDisplay
        :label="t('form.actual_rent_business.label')"
        :value="estateObject.actual_rent_business"
        format="currency" />
      <ValueDisplay
        :label="t('form.actual_rent_living.label')"
        :value="estateObject.actual_rent_living"
        format="currency" />
      <ValueDisplay
        :label="t('form.actual_rent_business_square_meter.label')"
        class="col-start-1"
        :value="estateObject.actual_rent_business_square_meter"
        format="currency" />
      <ValueDisplay
        :label="t('form.actual_rent_living_square_meter.label')"
        :value="estateObject.actual_rent_living_square_meter"
        format="currency" />
      <ValueDisplay :label="t('form.actual_rent.label')" :value="estateObject.actual_rent" format="currency" />
    </FormGroup>

    <FormGroup :legend="t('form.expected_rent_group.label')">
      <ValueDisplay
        :label="t('form.expected_rent_business.label')"
        :value="estateObject.expected_rent_business"
        format="currency" />
      <ValueDisplay
        :label="t('form.expected_rent_living.label')"
        :value="estateObject.expected_rent_living"
        format="currency" />
      <ValueDisplay
        :label="t('form.expected_rent_business_square_meter.label')"
        class="col-start-1"
        :value="estateObject.expected_rent_business_square_meter"
        format="currency" />
      <ValueDisplay
        :label="t('form.expected_rent_living_square_meter.label')"
        :value="estateObject.expected_rent_living_square_meter"
        format="currency" />
      <ValueDisplay :label="t('form.expected_rent.label')" :value="estateObject.expected_rent" format="currency" />
    </FormGroup>

    <FormGroup :legend="t('form.additional_utilities_group.label')">
      <ValueDisplay
        :label="t('form.additional_utilities_business.label')"
        :value="estateObject.additional_utilities_business"
        format="currency" />
      <ValueDisplay
        :label="t('form.additional_utilities_living.label')"
        :value="estateObject.additional_utilities_living"
        format="currency" />
      <ValueDisplay
        :label="t('form.additional_utilities_monthly_extra.label')"
        :value="estateObject.additional_utilities_monthly_extra"
        format="currency" />
      <ValueDisplay
        :label="t('form.additional_utilities_monthly_normal.label')"
        :value="estateObject.additional_utilities_monthly_normal"
        format="currency" />
    </FormGroup>

    <LastPage />
  </div>
</template>

<style scoped>
.pagebreak {
  break-after: page;
}

@media screen {
  .pagebreak {
    margin-top: 5rem;
    border-top: 1px dashed #ccc;
    padding-top: 5rem;
  }
}
</style>

<i18n>
{
  "en": {
    "yes": "Yes",
    "no": "No",
    "title": "New Property",
    "form": {
      "title": {
        "label": "Title / Description"
      },
      "confirm": {
        "leave": "You have unsaved changes. Are you sure you want to leave?"
      },
      "is_purchase": {
        "label": "Type",
        "purchase": "Purchase",
        "portfolio": "Portfolio"
      },
      "address": {
        "label": "Address"
      },
      "estate_object_type": {
        "label": "Estate Object Type"
      },
      "owner": {
        "purchase": {
          "label": "Owner after purchase"
        },
        "portfolio": {
          "label": "Owner"
        }
      },
      "owner_after_sale": {
        "label": "Owner after Sale"
      },
      "property_size": {
        "label": "Property Size"
      },
      "living_space_size": {
        "label": "Living Space Size"
      },
      "business_space_size": {
        "label": "Business Space Size"
      },
      "business_type": {
        "label": "Business Type"
      },
      "purchase_price": {
        "label": "Purchase Price"
      },
      "land_register_charge": {
        "label": "Land Register Charge"
      },
      "construction_year": {
        "label": "Construction Year"
      },
      "broker_commission": {
        "label": "Broker Commission"
      },
      "notary_land_registry_fees": {
        "label": "Notary & Land Registry Fees"
      },
      "equity": {
        "label": "Equity"
      },
      "number_of_floors": {
        "label": "Number of Floors"
      },
      "number_of_parking_spaces": {
        "label": "Number of Parking Spaces"
      },
      "number_of_garages": {
        "label": "Number of Garage Lots"
      },
      "heating_type": {
        "label": "Heating Type"
      },
      "energy_source_type": {
        "label": "Energy Source Type"
      },
      "roof_type": {
        "label": "Roof Type"
      },
      "window_type": {
        "label": "Window Type"
      },
      "elevator_type": {
        "label": "Elevator Type"
      },
      "general": {
        "label": "General Information"
      },
      "details": {
        "label": "Details"
      },
      "attributes": {
        "label": "Attributes"
      },
      "has_monument_protection": {
        "label": "Monument Protection"
      },
      "has_gambling": {
        "label": "Gambling"
      },
      "has_red_light": {
        "label": "Red Light"
      },
      "has_leasehold": {
        "label": "Leasehold"
      },
      "land_rent": {
        "label": "Ground Rent"
      },
      "land_rent_rate": {
        "label": "Leasehold Rate"
      },
      "leasehold_carrier": {
        "label": "Leaseholder"
      },
      "has_mountain_damage_waiver": {
        "label": "Mountain Damage Waiver"
      },
      "mountain_damage_waiver_rate": {
        "label": "Mountain Damage Waiver Rate"
      },
      "mountain_damage_waiver_carrier": {
        "label": "Mountain Damage Waiver Carrier"
      },
      "has_homeowners_association": {
        "label": "Homeowners Association"
      },
      "co_ownership_share": {
        "label": "Own Shares"
      },
      "co_ownership_share_total": {
        "label": "Total Shares"
      },
      "has_energy_certificate": {
        "label": "Energy Certificate"
      },
      "units_group": {
        "label": "Units"
      },
      "actual_rent_group": {
        "label": "Actual Rent (per month)"
      },
      "actual_rent_business": {
        "label": "Actual Rent Business"
      },
      "actual_rent_living": {
        "label": "Actual Rent Living"
      },
      "actual_rent_business_square_meter": {
        "label": "Actual Rent Business (per m²)"
      },
      "actual_rent_living_square_meter": {
        "label": "Actual Rent Living (per m²)"
      },
      "actual_rent": {
        "label": "Total Actual Rent"
      },
      "expected_rent_group": {
        "label": "Expected Rent (per month)"
      },
      "expected_rent_business": {
        "label": "Expected Rent Business"
      },
      "expected_rent_living": {
        "label": "Expected Rent Living"
      },
      "expected_rent_business_square_meter": {
        "label": "Actual Rent Business (per m²)"
      },
      "expected_rent_living_square_meter": {
        "label": "Actual Rent Living (per m²)"
      },
      "expected_rent": {
        "label": "Total Expected Rent"
      },
      "additional_utilities_group": {
        "label": "Additional Utilities (per month)"
      },
      "additional_utilities_business": {
        "label": "Business"
      },
      "additional_utilities_living": {
        "label": "Living"
      },
      "additional_utilities_monthly_extra": {
        "label": "Non-recoverable"
      },
      "additional_utilities_monthly_normal": {
        "label": "Total Additional Utilities"
      },
      "submit": {
        "save": "Save",
        "update": "Update"
      }
    },
    "upload": {
      "title": "Upload Documents"
    },
    "units": {
      "square_meter": "m²"
    }
  },
  "de": {
    "yes": "Ja",
    "no": "Nein",
    "title": "Neue Immobilie",
    "form": {
      "title": {
        "label": "Titel / Beschreibung"
      },
      "confirm": {
        "leave": "Du hast ungespeicherte Änderungen. Willst du die Seite wirklich verlassen?"
      },
      "is_purchase": {
        "label": "Typ",
        "purchase": "Kauf",
        "portfolio": "Portfolio"
      },
      "address": {
        "label": "Adresse"
      },
      "estate_object_type": {
        "label": "Immobilienart"
      },
      "owner": {
        "purchase": {
          "label": "Eigentümer nach Kauf"
        },
        "portfolio": {
          "label": "Eigentümer"
        }
      },
      "owner_after_sale": {
        "label": "Eigentümer nach Verkauf"
      },
      "property_size": {
        "label": "Grundstücksgröße"
      },
      "living_space_size": {
        "label": "Wohnfläche"
      },
      "business_space_size": {
        "label": "Gewerbefläche"
      },
      "business_type": {
        "label": "Geschäftsart"
      },
      "purchase_price": {
        "label": "Kaufpreis"
      },
      "land_register_charge": {
        "label": "Grunderwerbsteuer"
      },
      "construction_year": {
        "label": "Baujahr"
      },
      "broker_commission": {
        "label": "Maklerprovision"
      },
      "notary_land_registry_fees": {
        "label": "Notar- und Grundbuchkosten"
      },
      "equity": {
        "label": "Eigenkapital"
      },
      "number_of_floors": {
        "label": "Anzahl der Stockwerke"
      },
      "number_of_parking_spaces": {
        "label": "Anzahl der Parkplätze"
      },
      "number_of_garages": {
        "label": "Anzahl der Garagenstellplätze"
      },
      "heating_type": {
        "label": "Heizungstyp"
      },
      "energy_source_type": {
        "label": "Energiequelle"
      },
      "roof_type": {
        "label": "Dachdeckung"
      },
      "window_type": {
        "label": "Fenstertyp"
      },
      "elevator_type": {
        "label": "Aufzugstyp"
      },
      "general": {
        "label": "Allgemeine Informationen"
      },
      "details": {
        "label": "Details"
      },
      "attributes": {
        "label": "Merkmale"
      },
      "has_monument_protection": {
        "label": "Denkmalschutz"
      },
      "has_gambling": {
        "label": "Glücksspiel"
      },
      "has_red_light": {
        "label": "Rotlicht"
      },
      "has_leasehold": {
        "label": "Erbpacht"
      },
      "land_rent": {
        "label": "Erbpachtrate (mtl.)"
      },
      "land_rent_rate": {
        "label": "Erbpachtzins"
      },
      "leasehold_carrier": {
        "label": "Erbpachtgeber"
      },
      "has_mountain_damage_waiver": {
        "label": "Bergschadensverzicht"
      },
      "mountain_damage_waiver_rate": {
        "label": "Bergschadensrate"
      },
      "mountain_damage_waiver_carrier": {
        "label": "Bergschadensverzichtsgeber"
      },
      "has_homeowners_association": {
        "label": "Eigentümergemeinschaft"
      },
      "co_ownership_share": {
        "label": "Eigene Anteile"
      },
      "co_ownership_share_total": {
        "label": "Gesamtanteile"
      },
      "has_energy_certificate": {
        "label": "Energieausweis"
      },
      "actual_rent_group": {
        "label": "Ist-Miete (pro Monat)"
      },
      "actual_rent_business": {
        "label": "Ist-Miete Gewerbe"
      },
      "actual_rent_living": {
        "label": "Ist-Miete Wohnen"
      },
      "actual_rent_business_square_meter": {
        "label": "Ist-Miete Gewerbe (pro m²)"
      },
      "actual_rent_living_square_meter": {
        "label": "Ist-Miete Wohnen (pro m²)"
      },
      "actual_rent": {
        "label": "Gesamt Ist-Miete"
      },
      "expected_rent_group": {
        "label": "Soll-Miete (pro Monat)"
      },
      "expected_rent_business": {
        "label": "Soll-Miete Gewerbe"
      },
      "expected_rent_living": {
        "label": "Soll-Miete Wohnen"
      },
      "expected_rent_business_square_meter": {
        "label": "Soll-Miete Gewerbe (pro m²)"
      },
      "expected_rent_living_square_meter": {
        "label": "Soll-Miete Wohnen (pro m²)"
      },
      "expected_rent": {
        "label": "Gesamt Soll-Miete"
      },
      "additional_utilities_group": {
        "label": "Nebenkosten (pro Monat)"
      },
      "additional_utilities_business": {
        "label": "Gewerbe"
      },
      "additional_utilities_living": {
        "label": "Wohnen"
      },
      "additional_utilities_monthly_extra": {
        "label": "Nicht umlagefähig"
      },
      "additional_utilities_monthly_normal": {
        "label": "Gesamte Nebenkosten"
      },
      "units_group": {
        "label": "Einheiten"
      },
      "submit": {
        "save": "Immobilie speichern",
        "update": "Änderungen speichern"
      }
    },
    "upload": {
      "title": "Dokumente hochladen"
    },
    "units": {
      "square_meter": "m²"
    }
  }
}

</i18n>
